import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import { FORM } from 'utils/constants/css';

const useStyles = makeStyles(theme => ({
  root: {
    border: FORM.BORDER,
  },
  formControl: {
    width: '100%',
    marginBottom: '1rem',
    display: 'flex',
  },
  label: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  input: {
    color: theme.palette.text.primary,
    backgroundColor: ({ disabled }) => {
      if (typeof disabled === 'boolean' && disabled) {
        return theme.palette.text.disabled;
      } else {
        return theme.palette.background.default;
      }
    },
    minHeight: '10rem',
    alignItems: 'flex-start',
    padding: '0.5rem',
    '&::placeholder': {
      color: theme.palette.text.primary,
      opacity: 0.5,
    },
  },
  helperText: {
    color: theme.palette.text.primary,
  },
}));

const TextArea = ({
  label,
  maxLength,
  className,
  required,
  error,
  onChange,
  value,
  onFocus,
  field,
  placeholder,
  info,
  disabled,
}) => {
  const { t } = useTranslation();
  const classes = useStyles({ disabled });

  const [textLength, setTextLength] = useState(
    value ? maxLength - value.length : maxLength,
  );

  const handleChange = event => {
    const remainingTextLength = maxLength - event.target.value.length;
    if (remainingTextLength >= 0) {
      onChange(event);
      setTextLength(remainingTextLength);
    }
  };
  const text = t('characters remaining');
  const remainingCharactersText = disabled ? '' : `${textLength} ${text}`;

  return (
    <FormControl
      className={cn(classes.formControl, className)}
      error={Boolean(error)}
    >
      <InputLabel
        shrink
        className={classes.label}
        required={required}
        htmlFor={`${label}-${field}`}
      >
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <Input
        inputProps={{
          maxLength: maxLength,
        }}
        classes={{
          input: classes.input,
          root: classes.root,
        }}
        rows={10}
        className={classes.input}
        disableUnderline={true}
        name={field}
        onFocus={onFocus}
        id={`${label}-${field}`}
        value={value}
        multiline
        onChange={handleChange}
        placeholder={t(placeholder)}
        aria-describedby="component-error-text"
        disabled={disabled}
      />
      <FormHelperText className={classes.helperText}>
        {remainingCharactersText}
      </FormHelperText>
    </FormControl>
  );
};

TextArea.propTypes = {
  label: PropTypes.string,
  maxLength: PropTypes.number,
  className: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  onFocus: PropTypes.func,
  field: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  info: InfoPropTypes,
};

TextArea.defaultProps = {
  maxLength: 1000,
};

export default TextArea;
